import React, { useMemo, useState } from "react";
import { OfferBannerWrapper } from "../../../atoms/offer-banner/offer-banner-wrapper";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import { googleLogout } from "@react-oauth/google";
import { BackArrowIcon, EditPencilIcon } from "./icons";
import "./index.m.css";
import { SUBSCRIBE_PAGE_TEXT } from "../../../../constants";
import { setIsLogin, setUserDetails } from "../../../store/actions";
import { SpinnerLoader } from "../../../common/loader";
import { clearAuthCookies } from "../../../helper/utils";

const formatDate = (date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const calculateDates = (plan) => {
  const startDate = new Date();
  const endDate = new Date(startDate);

  if (plan && plan.duration_length && plan.duration_unit) {
    const length = parseInt(plan.duration_length, 10);
    const unit = plan.duration_unit.toLowerCase();

    if (unit.includes('day')) {
      endDate.setDate(endDate.getDate() + length);
    } else if (unit.includes('week')) {
      endDate.setDate(endDate.getDate() + length * 7);
    } else if (unit.includes('month')) {
      endDate.setMonth(endDate.getMonth() + length);
    } else if (unit.includes('year')) {
      endDate.setFullYear(endDate.getFullYear() + length);
    }
  } else {
    // Default fallback to 1 year
    endDate.setFullYear(endDate.getFullYear() + 1);
  }

  return {
    start: formatDate(startDate),
    end: formatDate(endDate)
  };
};

export const ReviewPage = ({ plan, onBack, onPay, isPayDisabled = false, isPaying = false }) => {
  const { start, end } = useMemo(() => calculateDates(plan), [plan]);

  const cents = plan?.discounted_price_cents || plan?.price_cents || 0;
  const currencySymbol = plan?.price_currency === "INR" ? "₹" : plan?.price_currency === "USD" ? "$" : "";
  const exactPriceAmount = cents > 0 ? `${currencySymbol}${(cents / 100).toFixed(2)}` : "";
  const buttonPriceAmount = cents > 0 ? `${currencySymbol}${cents % 100 === 0 ? (cents / 100) : (cents / 100).toFixed(2)}` : "";

  const [isBannerVisible, setIsBannerVisible] = useState(false);

  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const ssoEndpoint = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], null)
  );
  const dispatch = useDispatch();

  const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
  const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.phone || userdetails?.userData?.phone || userdetails?.mobile;
  const loginDisplay = email || phone || "";

  const handleChangeAccount = () => {
    if (typeof window !== "undefined") {
      window.logoutInProgress = true;
      const { addLogoutToDataLayer } = require("../../../helper/datalayer-utils");
      addLogoutToDataLayer(window.location.href, userdetails, "change_account");

      window.localStorage.removeItem("user");
      window.localStorage.removeItem("access_token");
      window.localStorage.removeItem("refresh_token");
      window.localStorage.removeItem("commentsToken");
      window.localStorage.removeItem("last_logged_in_uuid");
      document.cookie = "qt-auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax";
      clearAuthCookies();
      sessionStorage.removeItem("subscribe_payment_processing_state");
      sessionStorage.removeItem("show_support_strip_on_redirect");

      dispatch(setIsLogin(false));
      dispatch(setUserDetails(null));

      googleLogout();
      
      let redirectUrl = window.location.origin + "/subscribe";
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get("returnUrl");

      if (returnUrl) {
        redirectUrl = returnUrl;
      }

      setTimeout(() => {
        window.location.search = "";
        window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${redirectUrl}`;
      }, 300);
    }
  };

  const handleEditPlanClick = () => {
    if (window) {
      const { getAuthStatus } = require("../../../helper/datalayer-utils");
      const { uuid } = getAuthStatus();
      
      const durationUnit = plan?.duration_unit || "year";
      const durationLength = plan?.duration_length || 1;
      let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
      if (planName === "1-month") planName = "monthly";

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "plan_edit",
        uuid: uuid,
        from_plan: plan?.title || planName,
        from_price: plan?.price_cents ? plan.price_cents / 100 : "NA",
        currency: plan?.price_currency || "INR"
      });
    }
    onBack();
  };

  const handleProceedToPayClick = () => {
    if (window) {
      const { getAuthStatus } = require("../../../helper/datalayer-utils");
      const { uuid } = getAuthStatus();
      
      const durationUnit = plan?.duration_unit || "year";
      const durationLength = plan?.duration_length || 1;
      let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
      if (planName === "1-month") planName = "monthly";

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "proceed_to_pay_click",
        uuid: uuid,
        plan_name: plan?.title || planName,
        plan_price: plan?.price_cents ? plan.price_cents / 100 : "NA",
        currency: plan?.price_currency || "INR"
      });
    }
    onPay(plan);
  };

  return (
    <div styleName="reviewPageContainer">
      {isPaying && (
        <div styleName="overlayLoader">
          <SpinnerLoader />
        </div>
      )}
      <div styleName={isBannerVisible ? "headerRow headerRowWithBanner" : "headerRow"}>
        <button styleName="backButton" aria-label="Go Back" onClick={onBack}>
          <BackArrowIcon styleName="backIcon" />
        </button>
        <h1 styleName="reviewTitle">{SUBSCRIBE_PAGE_TEXT.REVIEW_TITLE}</h1>
      </div>

      <OfferBannerWrapper onVisibilityChange={setIsBannerVisible} />

      <div styleName="contentWrapper">
        <div styleName="planDetailsBox">
          <div styleName="planHeaderRow">
            <h2 styleName="planName">{plan?.title}</h2>
            <div styleName="editButton" onClick={handleEditPlanClick}>
              <EditPencilIcon styleName="editIcon" />
              <span styleName="editText">{SUBSCRIBE_PAGE_TEXT.EDIT}</span>
            </div>
          </div>
          <div styleName="planDates">
            {SUBSCRIBE_PAGE_TEXT.START_DATE}{start}<br />
            {SUBSCRIBE_PAGE_TEXT.END_DATE}{end}
          </div>
        </div>

        <div styleName="paymentDetailsBox">
          <div styleName="amountGroup">
            <div styleName="totalAmountRow">
              <div styleName="totalAmountTextGroup">
                <span styleName="totalAmountLabel">{SUBSCRIBE_PAGE_TEXT.AMOUNT_LABEL}</span>
                <span styleName="totalAmountTax">{SUBSCRIBE_PAGE_TEXT.AMOUNT_TAX}</span>
              </div>
              <div styleName="totalAmountValue">{exactPriceAmount || ""}</div>
            </div>
            <div styleName="renewalText">
              {SUBSCRIBE_PAGE_TEXT.RENEWAL_TEXT}
            </div>
          </div>
          <div styleName="footerGroup">
            <button styleName="payButton" onClick={handleProceedToPayClick} disabled={isPayDisabled}>
              {isPaying ? "Processing..." : `${SUBSCRIBE_PAGE_TEXT.PROCEED_TO_PAY}${buttonPriceAmount ? ` ${buttonPriceAmount}` : ""}`}
            </button>
            <div styleName="loginTextRow">
              <span styleName="loginPrefix">{SUBSCRIBE_PAGE_TEXT.LOGIN_PREFIX}</span>
              <span styleName="loginEmail">{loginDisplay}</span>
              <span styleName="changeAccount" onClick={handleChangeAccount}>{SUBSCRIBE_PAGE_TEXT.CHANGE_ACCOUNT}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
